import { type DocumentNode } from '@apollo/client';
import {
  Modal,
  ModalContent,
  type ModalHook,
  ModalSize,
  useControlledModalState,
} from '@leland-dev/leland-ui-library';
import React, {
  type ComponentPropsWithoutRef,
  type FC,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { useAuth } from '../context/AuthContext';
import { isServer } from '../utils/constants';
import { BASE_SEARCH_PATH } from '../utils/search';

import { LoginFormV2 } from './forms/LoginFormV2';
import { SignupForm } from './forms/SignupForm';

type LoginModalMode = 'login' | 'signup';

interface LoginModalHookProps
  extends Pick<
    ComponentPropsWithoutRef<typeof ModalContent>,
    'preventCloseOnOverlayClick' | 'hideCloseButton'
  > {
  onApplicantLogin: () => void;
  onCoachLogin?: () => void;
  defaultMode?: LoginModalMode;
  refetchQueries?: DocumentNode[];
}

interface LoginModalProps extends LoginModalHookProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const LoginModalContent: FC<LoginModalProps> = ({
  open,
  setOpen,
  onApplicantLogin,
  onCoachLogin,
  defaultMode = 'signup',
  refetchQueries,
  ...modalContentProps
}) => {
  const [mode, setMode] = useState<LoginModalMode>(defaultMode);
  const { currentUser } = useAuth();
  const onCoachLoginWrapper = useCallback(() => {
    setMode('signup');
    if (onCoachLogin) {
      onCoachLogin();
    }
  }, [onCoachLogin]);
  useEffect(() => {
    if (currentUser?.applicant) {
      // already a logged-in customer, close the login modal
      onApplicantLogin();
    } else if (currentUser?.coach) {
      // a logged-in coach, prompt to sign up as customer
      onCoachLoginWrapper();
    }
  }, [
    currentUser?.applicant,
    currentUser?.coach,
    onApplicantLogin,
    onCoachLoginWrapper,
  ]);
  const redirectUrl = isServer ? BASE_SEARCH_PATH : window.location.href;

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalContent size={ModalSize.SMALL} {...modalContentProps}>
        <div className="p-6">
          {mode === 'login' ? (
            <LoginFormV2
              className="w-full text-center"
              returnTo={redirectUrl}
              isModal
              setMode={setMode}
              onApplicantModalLogin={onApplicantLogin}
              onCoachModalLogin={onCoachLoginWrapper}
              refetchQueries={refetchQueries}
            />
          ) : (
            <SignupForm
              className="w-full text-center"
              isModal
              setMode={setMode}
              onApplicantModalLogin={onApplicantLogin}
              onCoachModalLogin={onCoachLoginWrapper}
              refetchQueries={refetchQueries}
            />
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

export const useLoginModal: ModalHook<LoginModalHookProps> = () => {
  const [open, setOpen] = useControlledModalState();
  const LoginModal: FC<LoginModalHookProps> = (props) => {
    return open ? (
      <LoginModalContent {...props} open={open} setOpen={setOpen} />
    ) : null;
  };
  return { open, setOpen, Modal: LoginModal };
};
